import dayjs from 'dayjs'

import { parseServerDate } from '@/utils/dates'

/**
 * Default trial duration in days. Used for both initial invitations and
 * extensions until the SME-editable input lands in v2.
 */
export const TRIAL_DAYS = 31

/**
 * Human-readable label for the trial duration. Decoupled from TRIAL_DAYS so
 * marketing copy and the backend day count can drift independently.
 */
export const TRIAL_DURATION_LABEL = 'one month'

/**
 * Bounds for the SWI-Admin-only "Trial duration (days)" override on the
 * Add User modal. Kept here so any future caller (e.g. extend flows) can
 * share the same min/max.
 */
export const MIN_TRIAL_DAYS = 1
export const MAX_TRIAL_DAYS = 365

// SME-1154: off while Sidekick pays for trials; turn back on once channels pay for them.
export const RESTART_TRIAL_ENABLED = false

// 29-31 days reads as "one month"; anything else is stated in exact days.
// Null/invalid falls back to the default label so legacy members without the
// backend trialDays field don't render blank copy.
export function formatTrialDuration(days: number | null | undefined): string {
  if (days == null || !Number.isFinite(days) || days < MIN_TRIAL_DAYS) {
    return TRIAL_DURATION_LABEL
  }
  if (days >= 29 && days <= 31) return 'one month'
  return `${days} day${days === 1 ? '' : 's'}`
}

export type TrialState =
  | { kind: 'none' }
  | { kind: 'pending' }
  | { kind: 'active'; endsAt: Date; daysRemaining: number }
  | { kind: 'expired'; endedAt: Date }

export function getTrialState(user: { isTrial: boolean; trialEnd: string | null }): TrialState {
  if (!user.isTrial) return { kind: 'none' }
  if (!user.trialEnd) return { kind: 'pending' }

  const end = parseServerDate(user.trialEnd)
  if (!end.isValid()) return { kind: 'pending' }

  const now = dayjs()
  if (end.isBefore(now)) {
    return { kind: 'expired', endedAt: end.toDate() }
  }

  // trialEnd keeps the UTC time of day of the invite or restart (extensions add whole
  // days), so a fractional-day diff can ceil up to N+1. Compare calendar days instead.
  const today = now.startOf('day')
  const endDay = end.startOf('day')
  const daysRemaining = Math.max(1, endDay.diff(today, 'day'))

  return { kind: 'active', endsAt: end.toDate(), daysRemaining }
}
